import type { Ref } from 'vue'
import { onMounted, onUnmounted, ref, watch } from 'vue'
import type { ScrollChapter } from './useInfiniteChapter'

/**
 * 章节可见性检测 Composable
 * 使用 IntersectionObserver 检测章节进入/离开视口
 */

interface ChapterVisibilityOptions {
  chapters: Ref<ScrollChapter[]>
  containerRef: Ref<HTMLElement | undefined>
  contentId: Ref<string>
  onChapterEnter: (chapter: ScrollChapter) => void
  onChapterLeave: (chapter: ScrollChapter, direction: 'up' | 'down') => void
  onCurrentChapterChange: (chapterId: string) => void
}

export function useChapterVisibility(options: ChapterVisibilityOptions) {
  const {
    chapters,
    containerRef,
    contentId,
    onChapterEnter,
    onChapterLeave,
    onCurrentChapterChange,
  } = options

  const currentChapterId = ref<string>('')
  const visibleChapterIds = ref<Set<string>>(new Set())
  const isPaused = ref(false)

  let observer: IntersectionObserver | null = null

  // 记录每个章节的上一次可见状态，用于判断离开方向
  const lastScrollTop = ref(0)

  /**
   * 暂停可见性检测
   */
  function pause() {
    isPaused.value = true
  }

  /**
   * 恢复可见性检测，并立即重新计算当前章节
   */
  function resume() {
    isPaused.value = false
    // 恢复后立即重新计算当前可见章节
    determineCurrentChapter()
  }

  /**
   * 更新 URL（不刷新页面），保留现有的 query 参数
   */
  function updateUrlWithoutReload(chapterId: string) {
    const url = new URL(window.location.href)
    url.pathname = `/content/${contentId.value}/scroll/${chapterId}`
    history.replaceState({ chapterId }, '', url.href)
  }

  /**
   * 确定当前主要可见章节（占据视口最多的章节）
   */
  function determineCurrentChapter() {
    if (!containerRef.value)
      return

    const container = containerRef.value
    const containerRect = container.getBoundingClientRect()
    const containerTop = containerRect.top
    const containerBottom = containerRect.bottom
    const containerHeight = containerRect.height

    let maxVisibleArea = 0
    let mostVisibleChapterId = ''

    chapters.value.forEach((chapter) => {
      const el = container.querySelector(`[data-chapter-id="${chapter.id}"]`) as HTMLElement
      if (!el)
        return

      const rect = el.getBoundingClientRect()

      // 计算章节在容器内的可见区域
      const visibleTop = Math.max(rect.top, containerTop)
      const visibleBottom = Math.min(rect.bottom, containerBottom)
      const visibleHeight = Math.max(0, visibleBottom - visibleTop)

      if (visibleHeight > maxVisibleArea) {
        maxVisibleArea = visibleHeight
        mostVisibleChapterId = chapter.id
      }
    })

    if (mostVisibleChapterId && mostVisibleChapterId !== currentChapterId.value) {
      const oldChapterId = currentChapterId.value
      currentChapterId.value = mostVisibleChapterId

      // 更新 URL
      updateUrlWithoutReload(mostVisibleChapterId)

      // 触发章节变化回调
      onCurrentChapterChange(mostVisibleChapterId)
    }
    // 没有找到可见章节且当前未设置章节ID时（例如导语占满视口），默认使用第一个章节
    else if (!mostVisibleChapterId && !currentChapterId.value && chapters.value.length > 0) {
      currentChapterId.value = chapters.value[0].id
      updateUrlWithoutReload(chapters.value[0].id)
    }
  }

  /**
   * 创建 IntersectionObserver
   */
  function createObserver() {
    if (!containerRef.value)
      return

    observer = new IntersectionObserver(
      (entries) => {
        // 暂停时不处理
        if (isPaused.value)
          return

        const currentScrollTop = containerRef.value?.scrollTop || 0
        const scrollDirection = currentScrollTop > lastScrollTop.value ? 'down' : 'up'
        lastScrollTop.value = currentScrollTop

        entries.forEach((entry) => {
          const chapterId = entry.target.getAttribute('data-chapter-id')
          if (!chapterId)
            return

          const chapter = chapters.value.find(c => c.id === chapterId)
          if (!chapter)
            return

          if (entry.isIntersecting) {
            // 章节进入视口（任意部分可见即触发）
            if (!visibleChapterIds.value.has(chapterId)) {
              visibleChapterIds.value.add(chapterId)
              onChapterEnter(chapter)
            }
          }
          else {
            // 章节离开视口
            if (visibleChapterIds.value.has(chapterId)) {
              visibleChapterIds.value.delete(chapterId)
              // 根据滚动方向判断离开方向
              const direction = scrollDirection === 'down' ? 'up' : 'down'
              onChapterLeave(chapter, direction)
            }
          }
        })

        // 重新确定当前主要可见章节
        determineCurrentChapter()
      },
      {
        root: containerRef.value,
        threshold: [0, 0.1, 0.25, 0.5, 0.75, 1.0],
        rootMargin: '0px',
      },
    )

    // 观察所有章节元素
    observeAllChapters()
  }

  /**
   * 观察所有章节元素
   */
  function observeAllChapters() {
    if (!observer || !containerRef.value)
      return

    const chapterElements = containerRef.value.querySelectorAll('[data-chapter-id]')
    chapterElements.forEach((el) => {
      observer!.observe(el)
    })
  }

  /**
   * 停止观察所有章节元素
   */
  function unobserveAllChapters() {
    if (!observer)
      return

    observer.disconnect()
  }

  /**
   * 重新观察（当章节列表变化时调用）
   */
  function refresh() {
    unobserveAllChapters()
    observeAllChapters()
  }

  // 监听章节列表变化
  watch(
    () => chapters.value.length,
    () => {
      // 延迟执行，等待 DOM 更新
      setTimeout(refresh, 100)
    },
  )

  // 组件挂载时创建 observer
  onMounted(() => {
    // 等待容器 ref 就绪
    const checkAndCreate = () => {
      if (containerRef.value)
        createObserver()

      else
        setTimeout(checkAndCreate, 100)
    }
    checkAndCreate()
  })

  // 组件卸载时清理
  onUnmounted(() => {
    unobserveAllChapters()
    observer = null
  })

  return {
    currentChapterId,
    visibleChapterIds,
    refresh,
    pause,
    resume,
    determineCurrentChapter,
  }
}
